import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Client, Session } from '@/domain/schema/v1';
import { JOURNAL_FORM_FIELDS } from '@/domain/schema/v1';
import { detectTranscript, splitCombined } from '@/domain/transcript/detect';
import { JOURNAL_FIELD_LABELS } from '@/domain/workflow/journal';
import { RISK_LABELS } from '@/domain/workflow/brief';
import { goalStatusLabel } from '@/domain/workflow/documents';
import { suggestSessionNumber } from '@/domain/workflow/home';
import { localDate } from '@/domain/workflow/dates';
import { AiBridge } from '@/services/aiBridge';
import type { SessionTodo } from '@/services/counseling';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { useRequiredSession } from '../../state/session';
import { flushAll } from '../../state/saves';
import { ConfirmButton } from '../../components/fields';
import { ReceiveBox, SendButton } from '../../components/ExternalRoundTrip';
import { MemoBox } from './InputSection';
import { VerifyCard } from './VerifyScreen';
import { DirectionBox, ImportCard, SecondReview } from './AiScreen';
import { PendingOpinions } from '../client/FormulationScreen';
import { useTranscripts } from './TranscriptSection';
import { RangeDocMaker } from './DialogueMaker';
import { TreatmentLine } from '../../components/TreatmentLine';

/**
 * 회기 진행 (A31, 화면 개편 A34): 한 화면에 한 단계 — 1 전사 넣기 → 2 정리 → 3 AI → 4 마무리.
 * 위 막대로 언제든 단계를 옮기고, "자세히"는 회기 상세(그 회기의 모든 기록)로 간다.
 * 정리는 몇 초면 끝나므로 기다리는 화면 없이 바로 확인할 줄을 보여준다.
 */
type Step = 1 | 2 | 3 | 4 | 5;
const STEP_NAMES: Record<Step, string> = { 1: '전사 넣기', 2: '정리', 3: 'AI', 4: '검토', 5: '마무리' };
const STEPS: Step[] = [1, 2, 3, 4, 5];

function stepOf(s: Session): Step {
  if (s.status === 'draft') return 1;
  if (s.status === 'processing' || s.status === 'verification') return 2;
  if (s.status === 'approved') return 5;
  if (!s.processingState.aiAnalysisImported) return 3;
  return 4;
}

/** 내담자에서 시작: 아직 회기가 없으면 첫 전사를 넣는 순간 회기를 만든다 */
export function RunStartScreen() {
  const { clientId = '' } = useParams();
  const svc = useService();
  const navigate = useNavigate();
  const { data } = useLoad(async () => {
    const [client, sessions] = await Promise.all([svc.getClient(clientId), svc.listSessions(clientId)]);
    return { client, sessions };
  }, [svc, clientId]);
  const open = data?.sessions.filter((s) => s.status !== 'approved').at(-1);
  // 끝나지 않은 회기가 있으면 그 회기로
  useEffect(() => {
    if (open) navigate(`/sessions/${open.id}/run`, { replace: true });
  }, [open, navigate]);
  const [number, setNumber] = useState<number | null>(null);
  const [date, setDate] = useState(localDate());
  const [err, setErr] = useState<string | null>(null);
  if (!data || open) return <p className="muted">불러오는 중…</p>;
  const n = number ?? suggestSessionNumber(data.sessions);
  const start = (put: (s: Session) => Promise<unknown>) =>
    void svc
      .startSession(clientId, { sessionNumber: n, sessionDate: date })
      .then(async (s) => {
        await put(s);
        navigate(`/sessions/${s.id}/run`, { replace: true });
      })
      .catch((e: Error) => setErr(e.message));
  return (
    <RunFrame client={data.client} label={`${n}회기`} step={1} reached={1} title="전사 파일을 넣어 주세요">
      <div className="run-session-line">
        <label>
          회기
          <input
            type="number"
            min={1}
            value={n}
            onChange={(e) => setNumber(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
        <label>
          상담 날짜
          <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        </label>
      </div>
      <DeviceCards galaxy={null} iphone={null} />
      <TranscriptDrop
        galaxy={false}
        iphone={false}
        onTexts={(items) =>
          start(async (s) => {
            for (const it of items)
              for (const [src, t] of slotsFor(it.text, it.slot)) await svc.saveRawTranscript(s.id, src, t);
          })
        }
      />
      <button type="button" className="link small" onClick={() => start(async () => undefined)}>
        전사 없이 메모로만 시작
      </button>
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
    </RunFrame>
  );
}

/** 넣은 글이 어느 기기 전사인지 (두 기기가 섞여 있으면 나눔) */
function slotsFor(text: string, forced?: 'samsung_raw' | 'apple_raw'): ['samsung_raw' | 'apple_raw', string][] {
  if (forced) return [[forced, text]];
  const kind = detectTranscript(text).kind;
  if (kind === 'combined') {
    const s = splitCombined(text);
    if (s)
      return [
        ['samsung_raw', s.galaxy],
        ['apple_raw', s.iphone],
      ];
  }
  return [[kind === 'iphone' ? 'apple_raw' : 'samsung_raw', text]];
}

export function RunScreen() {
  const { sessionId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const svc = useService();
  const { data, reload } = useLoad(async () => {
    await svc.resumeIfNeeded(sessionId);
    const session = await svc.getSession(sessionId);
    const [client, sessions] = await Promise.all([svc.getClient(session.clientId), svc.listSessions(session.clientId)]);
    return { session, client, sessions };
  }, [svc, sessionId]);
  // 다른 화면·기기에서 바뀌어도 따라온다
  useEffect(() => {
    const on = (e: Event) => {
      const t = (e as CustomEvent<{ entityType?: string }>).detail?.entityType;
      if (t === 'Session' || t === 'Transcript') reload();
    };
    window.addEventListener('counselai:data-changed', on);
    return () => window.removeEventListener('counselai:data-changed', on);
  }, [reload]);
  if (!data) return <p className="muted">불러오는 중…</p>;
  const { session, client, sessions } = data;
  const reached = stepOf(session);
  const asked = Number(params.get('step')) as Step;
  const step: Step = asked >= 1 && asked <= 5 ? asked : reached;
  const go = (s: Step) => setParams({ step: String(s) });
  const frame = {
    client,
    label: `${session.sessionNumber}회기`,
    step,
    reached,
    onStep: go,
    session,
    sessions,
  };
  return (
    <>
      {step === 1 && <StepInput frame={frame} session={session} onNext={() => go(2)} onChanged={reload} />}
      {step === 2 && <StepProcess frame={frame} session={session} onNext={() => go(3)} onChanged={reload} />}
      {step === 3 && <StepAi frame={frame} session={session} client={client} onNext={() => go(4)} onChanged={reload} />}
      {step === 4 && <StepReview frame={frame} session={session} onNext={() => go(5)} />}
      {step === 5 && <StepFinish frame={frame} session={session} onChanged={reload} />}
    </>
  );
}

type FrameProps = {
  client: Client;
  label: string;
  step: Step;
  reached: Step;
  onStep?: (s: Step) => void;
  session?: Session;
  sessions?: Session[];
};

/** 위: 누구·몇 회기(바꾸기) · 단계 막대 · 자세히 / 가운데: 큰 제목과 내용 / 아래: 다음 버튼 */
function RunFrame({
  client,
  label,
  step,
  reached,
  onStep,
  session,
  sessions,
  title,
  sub,
  cta,
  aside,
  children,
}: FrameProps & {
  title: string;
  sub?: React.ReactNode;
  cta?: React.ReactNode;
  /** PC·태블릿에서는 오른쪽 칸, 폰에서는 내용 아래 */
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const approved = session?.status === 'approved';
  return (
    <section className="run">
      <div className="run-top">
        <Link to={`/clients/${client.id}`} className="run-back" aria-label="내담자로">
          ‹
        </Link>
        {session && sessions && sessions.length > 1 ? (
          <label className="run-who">
            <span className="sr-only">회기 바꾸기</span>
            <select value={session.id} onChange={(e) => navigate(`/sessions/${e.target.value}/run`)}>
              {[...sessions]
                .sort((a, b) => a.sessionNumber - b.sessionNumber)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {client.clientCode} · {s.sessionNumber}회기 ({s.sessionDate.slice(5).replace('-', '/')})
                  </option>
                ))}
            </select>
          </label>
        ) : (
          <span className="run-who">
            {client.clientCode} · {label}
          </span>
        )}
        {session ? (
          <Link to={`/sessions/${session.id}`} className="run-more">
            자세히
          </Link>
        ) : (
          <span />
        )}
      </div>
      <ol className="run-bar" aria-label="회기 진행 단계">
        {STEPS.map((s) => {
          const done = approved || s < reached;
          return (
            <li key={s}>
              <button
                type="button"
                aria-current={s === step ? 'step' : undefined}
                className={done ? 'done' : undefined}
                disabled={!onStep || s > Math.max(reached === 4 ? 5 : reached, step)}
                onClick={() => onStep?.(s)}
              >
                <span className="seg" aria-hidden />
                {s} {STEP_NAMES[s]}
              </button>
            </li>
          );
        })}
      </ol>
      <div className="run-main">
        <div className="run-body">
          <h1 className="page-title">{title}</h1>
          {sub && <div className="muted">{sub}</div>}
          {children}
          {cta && <div className="run-cta">{cta}</div>}
        </div>
        {aside && <aside className="run-aside">{aside}</aside>}
      </div>
    </section>
  );
}

// ---------------- 1. 전사 넣기 ----------------

function StepInput({
  frame,
  session,
  onNext,
  onChanged,
}: {
  frame: FrameProps;
  session: Session;
  onNext: () => void;
  onChanged: () => void;
}) {
  const svc = useService();
  const { data: check, reload } = useLoad(
    () => svc.transcriptInputCheck(session.id),
    [svc, session.id, session.revision],
  );
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const put = (items: DropItem[]) => {
    setErr(null);
    setMsg(null);
    void (async () => {
      for (const it of items)
        for (const [src, t] of slotsFor(it.text, it.slot)) await svc.saveRawTranscript(session.id, src, t);
    })()
      .then(() => {
        setMsg('넣었습니다.');
        reload();
        onChanged();
      })
      .catch((e: Error) => setErr(e.message));
  };
  const any = !!check && (check.galaxy.present || check.iphone.present);
  const both = !!check && check.galaxy.present && check.iphone.present;
  // 정리는 몇 초면 끝나므로 바로 하고 2단계(확인할 줄)로 간다
  const process = () => {
    setBusy(true);
    setErr(null);
    void flushAll()
      .then(() =>
        session.status === 'draft' || session.status === 'processing' ? svc.processSession(session.id) : undefined,
      )
      .then(() => {
        onChanged();
        onNext();
      })
      .catch((e: Error) => setErr(`정리하지 못했습니다: ${e.message}. 다시 누르면 이어서 합니다.`))
      .finally(() => setBusy(false));
  };
  return (
    <RunFrame
      {...frame}
      title="전사 파일을 넣어 주세요"
      aside={<InputAside check={check} sessionId={session.id} />}
      cta={
        <>
          {any && !both && (
            <p className="muted small">
              한 기기 전사만 있어도 진행할 수 있어요. 다른 기기에서 올리면 동기화로 들어옵니다.
            </p>
          )}
          <button type="button" className="primary run-next" disabled={!any || busy} onClick={process}>
            {busy ? '정리하는 중…' : any ? '정리하기' : '전사를 넣으면 다음으로 갈 수 있어요'}
          </button>
        </>
      }
    >
      <PrepLink session={session} />
      <SessionLine session={session} onChanged={onChanged} />
      <DeviceCards galaxy={check ? check.galaxy : null} iphone={check ? check.iphone : null} />
      <TranscriptDrop galaxy={!!check?.galaxy.present} iphone={!!check?.iphone.present} onTexts={put} />
      {msg && <p className="ok small">{msg}</p>}
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
    </RunFrame>
  );
}

function InputAside({
  check,
  sessionId,
}: {
  check: { notes: { text: string; level: string }[] } | undefined;
  sessionId: string;
}) {
  return (
    <>
      {check && check.notes.length > 0 && (
        <div className="card">
          <strong>넣은 전사 점검</strong>
          <ul className="run-checks">
            {check.notes.map((n) => (
              <li key={n.text} className={n.level}>
                <span aria-hidden>{n.level === 'ok' ? '✓' : '!'}</span>
                {n.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      <MemoBox sessionId={sessionId} />
    </>
  );
}

/** 지난 회기에 세운 이번 회기 준비 자료가 있으면 한 줄로 (상담 전·중에 보기) */
function PrepLink({ session }: { session: Session }) {
  const svc = useService();
  const { data: brief } = useLoad(() => svc.previousBriefFor(session), [svc, session.id]);
  if (!brief) return null;
  const goal = brief.sessionObjectives?.[0];
  return (
    <Link className="prep-link" to={`/clients/${session.clientId}/brief?target=${session.sessionNumber}`}>
      <span className="li-main">
        <span className="li-title">이번 회기 준비 보기</span>
        <span className="li-sub">{goal ? `목표: ${goal}` : '지난 회기에 세운 계획·확인할 질문'}</span>
      </span>
      <span className="li-go">→</span>
    </Link>
  );
}

/** 갤럭시 전사 / 아이폰 전사: 같은 모양으로 들어왔는지·길이·앱 이름 */
function DeviceCards({
  galaxy,
  iphone,
}: {
  galaxy: { present: boolean; minutes: number | null } | null;
  iphone: { present: boolean; minutes: number | null } | null;
}) {
  return (
    <div className="device-lights" aria-label="들어온 전사">
      <Light on={!!galaxy?.present} label="갤럭시 전사" app="기본 녹음 앱" minutes={galaxy?.minutes ?? null} />
      <Light on={!!iphone?.present} label="아이폰 전사" app="다글로" minutes={iphone?.minutes ?? null} />
    </div>
  );
}

function Light({ on, label, app, minutes }: { on: boolean; label: string; app: string; minutes: number | null }) {
  return (
    <div className={`light ${on ? 'on' : ''}`}>
      <span className="bulb" aria-hidden>
        {on ? '✓' : ''}
      </span>
      <span className="light-text">
        <strong>{label}</strong>
        <span className="small">
          {on ? `들어옴${minutes ? ` · ${minutes}분` : ''}` : '아직 없음'} · {app}
        </span>
      </span>
    </div>
  );
}

/** 파일 선택(여러 개)·끌어다 놓기·붙여넣기. 어느 기기 전사인지는 알아서 구분하고, 모르면 묻는다 */
type DropItem = { text: string; slot?: 'samsung_raw' | 'apple_raw' };

function TranscriptDrop({
  galaxy,
  iphone,
  onTexts,
}: {
  galaxy: boolean;
  iphone: boolean;
  /** 한 번에 넣은 것(파일 여러 개 포함)을 한꺼번에 넘긴다 */
  onTexts: (items: DropItem[]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pasting, setPasting] = useState(false);
  const [paste, setPaste] = useState('');
  const [unknown, setUnknown] = useState<string | null>(null);
  const takeAll = (texts: string[]) => {
    const known = texts.filter((t) => t.trim() && detectTranscript(t).kind !== 'unknown');
    const odd = texts.find((t) => t.trim() && detectTranscript(t).kind === 'unknown');
    if (known.length) onTexts(known.map((text) => ({ text })));
    if (odd) setUnknown(odd);
  };
  const readFiles = (files: FileList | null) => {
    if (!files || !files.length) return;
    void Promise.all(Array.from(files).map((f) => f.text())).then(takeAll);
  };
  return (
    <div
      className="drop-zone"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        readFiles(e.dataTransfer.files);
      }}
    >
      <div className="drop-buttons">
        <button type="button" className="soft-btn" onClick={() => fileRef.current?.click()}>
          파일 고르기
        </button>
        <button type="button" className="soft-btn" onClick={() => setPasting((x) => !x)}>
          붙여넣기
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".txt,text/plain"
          multiple
          hidden
          aria-label="전사 파일"
          onChange={(e) => {
            readFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      <p className="muted small">
        {galaxy && iphone
          ? '두 전사가 모두 들어왔어요. 바꾸려면 다시 넣으세요.'
          : '여러 파일을 한 번에 골라도 되고, PC에서는 여기에 끌어다 놓아도 돼요. 어느 기기 전사인지는 형식으로 알아서 구분해요.'}
      </p>
      {pasting && (
        <div className="stack">
          <textarea
            aria-label="전사 붙여넣기"
            rows={6}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder="전사 전체를 붙여넣으세요"
          />
          <button
            type="button"
            disabled={!paste.trim()}
            onClick={() => {
              takeAll([paste]);
              setPaste('');
              setPasting(false);
            }}
          >
            넣기
          </button>
        </div>
      )}
      {unknown && (
        <div className="ask-card">
          <strong>이 파일은 어느 기기 전사인가요?</strong>
          <span className="small">형식으로 구분이 안 될 때만 물어요</span>
          <div className="row">
            <button
              type="button"
              className="pill-btn"
              onClick={() => (onTexts([{ text: unknown, slot: 'samsung_raw' }]), setUnknown(null))}
            >
              갤럭시 전사
            </button>
            <button
              type="button"
              className="pill-btn"
              onClick={() => (onTexts([{ text: unknown, slot: 'apple_raw' }]), setUnknown(null))}
            >
              아이폰 전사
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 회기 번호·날짜: 언제든 고칠 수 있고, 날짜 순서와 번호가 안 맞으면 알려준다 */
function SessionLine({ session, onChanged }: { session: Session; onChanged: () => void }) {
  const svc = useService();
  const [n, setN] = useState(String(session.sessionNumber));
  const [err, setErr] = useState<string | null>(null);
  const { data: byDate, reload } = useLoad(() => svc.dateOrderNumber(session.id), [svc, session.id, session.revision]);
  const saveNumber = () => {
    const v = Number(n);
    if (v === session.sessionNumber) return;
    void svc
      .setSessionNumber(session.id, v)
      .then(() => {
        setErr(null);
        onChanged();
      })
      .catch((e: Error) => {
        setErr(e.message);
        setN(String(session.sessionNumber));
      });
  };
  return (
    <div className="stack">
      <div className="run-session-line">
        <label>
          회기
          <input type="number" min={1} value={n} onChange={(e) => setN(e.target.value)} onBlur={saveNumber} />
        </label>
        <label>
          상담 날짜
          <input
            type="date"
            value={session.sessionDate}
            onChange={(e) =>
              e.target.value &&
              void svc.updateSession(session.id, { sessionDate: e.target.value }).then(() => {
                reload();
                onChanged();
              })
            }
          />
        </label>
        <label>
          시간(분)
          <input
            type="number"
            min={1}
            max={600}
            defaultValue={session.durationMinutes ?? ''}
            onBlur={(e) => {
              const v = Number(e.target.value);
              if (v && v !== session.durationMinutes)
                void svc.updateSession(session.id, { durationMinutes: v }).then(onChanged);
            }}
          />
        </label>
        <label>
          장소
          <input
            defaultValue={session.location ?? ''}
            placeholder="상담실"
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (session.location ?? ''))
                void svc.updateSession(session.id, { location: v || undefined }).then(onChanged);
            }}
          />
        </label>
      </div>
      <p className="muted small">번호·날짜는 언제든 고칠 수 있어요. 겹치는 번호는 막아요.</p>
      {err && <p className="warn small">{err}</p>}
      {byDate !== undefined && byDate !== session.sessionNumber && (
        <p className="warn small row">
          <span>
            날짜 순서로는 {byDate}회기입니다 (지금 {session.sessionNumber}회기).
          </span>
          <ConfirmButton
            label="날짜 순서대로 번호 다시 매기기"
            confirmText="이 내담자의 모든 회기 번호를 날짜 순서로 다시 매깁니다 (이미 만든 문서에 적힌 번호는 그대로)."
            onConfirm={() => void svc.renumberSessionsByDate(session.clientId).then(onChanged)}
          />
        </p>
      )}
    </div>
  );
}

// ---------------- 2. 정리 ----------------

function StepProcess({
  frame,
  session,
  onNext,
  onChanged,
}: {
  frame: FrameProps;
  session: Session;
  onNext: () => void;
  onChanged: () => void;
}) {
  const svc = useService();
  const ts = useTranscripts();
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const processed = session.status !== 'draft' && session.status !== 'processing';
  const { data: items, reload } = useLoad(
    async () => (processed ? ts.reviewItems(session) : []),
    [ts, session.id, session.revision, processed],
  );
  const run = () => {
    setRunning(true);
    setErr(null);
    void flushAll()
      .then(() => svc.processSession(session.id))
      .then(() => onChanged())
      .catch((e: Error) => setErr(`정리하지 못했습니다: ${e.message}. 다시 누르면 이어서 합니다.`))
      .finally(() => setRunning(false));
  };
  const next = () =>
    void (session.status === 'verification' ? svc.completeVerification(session.id) : Promise.resolve()).then(() => {
      onChanged();
      onNext();
    });
  const n = items?.length ?? 0;
  if (!processed)
    return (
      <RunFrame
        {...frame}
        title="정리할 준비가 됐어요"
        cta={
          <button type="button" className="primary run-next" disabled={running} onClick={run}>
            {running ? '정리하는 중…' : '정리하기'}
          </button>
        }
      >
        <p className="muted">두 전사를 맞추고, 상담자·내담자를 구분하고, 확인할 줄을 찾아요. 몇 초면 끝나요.</p>
        {err && (
          <p role="alert" className="warn small">
            {err}
          </p>
        )}
      </RunFrame>
    );
  return (
    <RunFrame
      {...frame}
      title={n ? `확인할 줄이 ${n}개 있어요` : '확인할 줄이 없어요'}
      aside={<ProcessAside sessionId={session.id} />}
      cta={
        <>
          <button type="button" className="primary run-next" onClick={next}>
            {n ? '확인 끝 · AI로' : 'AI로'}
          </button>
          {n > 0 && (
            <button type="button" className="link small" onClick={next}>
              나중에 확인하고 넘어가기
            </button>
          )}
        </>
      }
    >
      <p className="done-strip">✓ 두 전사를 맞추고, 상담자·내담자를 구분했어요.</p>
      {n > 0 && (
        <p className="muted small">
          두 기기가 다르게 받아 적었거나 누가 말했는지 확실하지 않은 줄만 모았어요. 확인하지 않은 줄은 "확인 필요"로
          남고 내담자 진술의 강한 근거로 쓰이지 않아요.
        </p>
      )}
      {items?.map(({ segment, discrepancy }) => (
        <VerifyCard key={segment.segmentId} session={session} segment={segment} d={discrepancy} onDone={reload} />
      ))}
    </RunFrame>
  );
}

function ProcessAside({ sessionId }: { sessionId: string }) {
  return (
    <div className="list-card">
      <Link className="list-item" to={`/sessions/${sessionId}#transcript`}>
        <span className="li-main">
          <span className="li-title">전사 전체 보기</span>
          <span className="li-sub">화자 표시 · 줄 고치기</span>
        </span>
        <span className="li-go">→</span>
      </Link>
      <Link className="list-item" to={`/sessions/${sessionId}?ranges=1`}>
        <span className="li-main">
          <span className="li-title">중요 구간 보기</span>
          <span className="li-sub">축어록·대화록 후보</span>
        </span>
        <span className="li-go">→</span>
      </Link>
    </div>
  );
}

// ---------------- 3. AI ----------------

function StepAi({
  frame,
  session,
  client,
  onNext,
  onChanged,
}: {
  frame: FrameProps;
  session: Session;
  client: Client;
  onNext: () => void;
  onChanged: () => void;
}) {
  const svc = useService();
  const app = useRequiredSession();
  const ai = useMemo(() => new AiBridge(app.repo, app.ctx, svc), [app, svc]);
  const { data, reload } = useLoad(async () => {
    const imports = await ai.listImports(session.id);
    return {
      packet: await ai.sessionPacket(session.id),
      imports: imports.filter((x) => x.purpose === 'session-analysis'),
      reviews: imports.filter((x) => x.purpose === 'second-review'),
      todo: await svc.sessionTodo(session.id),
    };
  }, [ai, svc, session.id, session.revision]);
  const latest = data?.imports.at(-1);
  const applied = !!latest?.appliedAt;
  const sent = !!session.processingState.aiPacketCreated;
  const roles = data?.todo.filter((t) => t.kind === 'roles') ?? [];
  const approvals = data?.todo.filter((t) => t.kind !== 'roles').length ?? 0;
  return (
    <RunFrame
      {...frame}
      title={applied ? '반영했어요' : '외부 AI에 보내고 답을 가져와요'}
      cta={
        <button type="button" className={applied ? 'primary run-next' : 'run-next'} onClick={onNext}>
          {applied ? (approvals ? `검토로 → (승인할 것 ${approvals}개)` : '검토로 →') : 'AI 없이 검토로'}
        </button>
      }
    >
      <DirectionBox client={client} onSaved={onChanged} collapsed />
      {!data && <p className="muted">회기 자료 준비 중…</p>}
      {data && !applied && (
        <>
          <div className="card num-card">
            <div className="num-head">
              <span className="num">1</span>
              <strong>외부로 보내기</strong>
              {sent && <span className="ok small">✓ 보냄</span>}
            </div>
            <p className="muted small">
              자료를 복사하고 {client.clientCode} 프로젝트를 열어요. 그 프로젝트 입력창에 붙여넣기만 하면 돼요.
            </p>
            <SendButton
              text={data.packet}
              projectUrl={client.externalProjectUrl}
              label="외부로 보내기"
              onSent={() => void ai.markPacketCopied(session.id).then(onChanged)}
            />
          </div>
          <div className="num-card-wrap">
            <div className="num-head">
              <span className="num">2</span>
              <strong>답 가져오기</strong>
            </div>
            {!latest || latest.parseStatus === 'failed' ? (
              <ReceiveBox
                title="외부 답 가져오기"
                hint="답 전체를 복사해 두면 돌아왔을 때 알아서 찾습니다."
                onSubmit={(raw) => ai.importSessionAnalysis(session.id, raw).then(reload)}
              />
            ) : (
              <p className="ok small">✓ 답을 가져왔어요</p>
            )}
          </div>
          {latest && (
            <div className="num-card-wrap">
              <div className="num-head">
                <span className="num">3</span>
                <strong>반영할 내용</strong>
                <Link to={`/sessions/${session.id}/ai`} className="small">
                  전체 보기
                </Link>
              </div>
              <ImportCard
                imp={latest}
                ai={ai}
                latest
                onChanged={() => {
                  reload();
                  onChanged();
                }}
              />
            </div>
          )}
        </>
      )}
      {data && applied && (
        <>
          <p className="done-strip">✓ 회기 분석·근거·상담기록지 초안·개정안이 채워졌어요.</p>
          {roles.length > 0 && <ReviewList session={session} items={roles} onDone={reload} title="AI 답 맞추기" />}
          <p className="muted small">
            {approvals
              ? `위험·목표·사례개념화·진단적 고려 승인 ${approvals}개는 다음 검토 단계에서 한 번에 해요.`
              : '승인할 것이 없어요.'}
          </p>
          <div className="card stack">
            <strong>AI에 다시 보내기</strong>
            <p className="muted small" style={{ margin: 0 }}>
              다시 분석하고 싶거나(전사를 고쳤을 때, 요청 방식이 바뀌었을 때) 답을 고쳐서 넣고 싶을 때. 새 답을 가져오면
              미리 보고 다시 반영해요. 이미 승인한 기록은 바뀌지 않고, 바뀌는 사례개념화·진단은 새 개정안으로 들어가요.
            </p>
            <div className="row">
              <SendButton
                text={data.packet}
                projectUrl={client.externalProjectUrl}
                label="외부로 다시 보내기"
                onSent={() => void ai.markPacketCopied(session.id).then(onChanged)}
              />
            </div>
            <ReceiveBox
              title="새 답 가져오기"
              hint="같은 회기 결과를 다시 넣으면 중복 없이 바뀝니다. 답 전체를 복사해 두면 돌아왔을 때 알아서 찾습니다."
              onSubmit={(raw) => ai.importSessionAnalysis(session.id, raw).then(reload)}
            />
          </div>
          <details className="card">
            <summary className="small">2차 검토 받기 (선택)</summary>
            <SecondReview
              sessionId={session.id}
              ai={ai}
              reviews={data.reviews}
              onChanged={reload}
              projectUrl={client.externalProjectUrl}
            />
          </details>
        </>
      )}
    </RunFrame>
  );
}

const RISK_KO = RISK_LABELS as Record<string, string>;

/** 확인·승인 목록: 마무리에서 모두, AI 단계에서는 화자 맞추기만 */
function ReviewList({
  session,
  items,
  onDone,
  title,
}: {
  session: Session;
  items: SessionTodo[];
  onDone: () => void;
  title: string;
}) {
  const svc = useService();
  const [undo, setUndo] = useState<{ text: string; run: () => Promise<unknown> } | null>(null);
  if (!items.length && !undo) return null;
  return (
    <div className="card review-list-card">
      <strong>
        {title} {items.length}
      </strong>
      {undo && (
        <p className="small undo-line" role="status">
          {undo.text}{' '}
          <button
            type="button"
            className="link"
            onClick={() =>
              void undo.run().then(() => {
                setUndo(null);
                onDone();
              })
            }
          >
            되돌리기
          </button>
        </p>
      )}
      {items.map((t) => {
        if (t.kind === 'risk')
          return (
            <div key="risk" className="todo-item risk">
              <strong>위험 평가</strong>
              <span className="small">
                {t.present.length ? `있음: ${t.present.map((k) => RISK_KO[k]).join(', ')}` : ''}
                {t.unclear.length ? ` 불분명: ${t.unclear.map((k) => RISK_KO[k]).join(', ')}` : ''}
              </span>
              <div className="row">
                <button type="button" className="primary" onClick={() => void svc.approveRisk(t.id).then(onDone)}>
                  확인하고 승인
                </button>
                <Link className="button" to={`/sessions/${session.id}#risk`}>
                  고치기
                </Link>
              </div>
            </div>
          );
        if (t.kind === 'goal')
          return (
            <div key={t.id} className="todo-item">
              <strong>목표 진행: {t.title}</strong>
              <span className="small">
                {goalStatusLabel(t.from as never)} → {goalStatusLabel(t.to as never)}
                {t.note ? ` · ${t.note}` : ''}
              </span>
              <div className="row">
                <button
                  type="button"
                  className="primary"
                  onClick={() => void svc.approveGoalProgress(t.id).then(onDone)}
                >
                  승인
                </button>
                <Link className="button" to={`/sessions/${session.id}#goals`}>
                  고치기
                </Link>
              </div>
            </div>
          );
        if (t.kind === 'roles') return <RoleFixItem key="roles" session={session} count={t.count} onDone={onDone} />;
        if (t.kind === 'formulation')
          return (
            <FormulationApprove
              key={t.id}
              clientId={session.clientId}
              item={t}
              onDone={onDone}
              onDiscard={() =>
                void svc.discardFormulationProposal(t.id).then(() => {
                  setUndo({
                    text: `사례개념화 개정안 v${t.version}을 버렸어요.`,
                    run: () => svc.restoreFormulationProposal(t.id),
                  });
                  onDone();
                })
              }
            />
          );
        return (
          <div key={t.id} className="todo-item">
            <strong>진단적 고려 개정안 v{t.version}</strong>
            <span className="small">{t.items.length ? t.items.join(' · ') : '항목 없음'} · 공식 진단이 아니에요</span>
            <div className="row">
              <ConfirmButton
                className="primary"
                label="승인"
                confirmText="승인하면 이 버전이 현재 진단적 고려가 됩니다. 이전 버전은 그대로 보존됩니다."
                onConfirm={() => void svc.approveDiagnosis(t.id).then(onDone)}
              />
              <Link className="button" to={`/clients/${session.clientId}/diagnosis`}>
                자세히 보고 고치기
              </Link>
              <ConfirmButton
                className="link small"
                label="개정안 버리기"
                confirmText="이 진단적 고려 개정안을 버릴까요? 버린 직후에는 되돌릴 수 있어요."
                onConfirm={() =>
                  void svc.discardDiagnosisProposal(t.id).then(() => {
                    setUndo({
                      text: `진단적 고려 개정안 v${t.version}을 버렸어요.`,
                      run: () => svc.restoreDiagnosisProposal(t.id),
                    });
                    onDone();
                  })
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const ROLE_KO: Record<string, string> = { counselor: '상담자', client: '내담자', unknown: '모름', mixed: '섞임' };
const mmss = (ms: number) => {
  const t = Math.floor(ms / 1000);
  const h = Math.floor(t / 3600);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${p2(Math.floor((t % 3600) / 60))}:${p2(t % 60)}` : `${p2(Math.floor(t / 60))}:${p2(t % 60)}`;
};

/**
 * AI가 고친 화자: "줄 보기"를 누르면 그 자리에서 고친 줄을 보여주고(시각·발언·AI가 댄 이유·앞뒤 한 줄),
 * 줄마다 상담자/내담자를 눌러 확정한다. 확정한 줄은 목록에서 빠진다. 30줄씩.
 */
function RoleFixItem({ session, count, onDone }: { session: Session; count: number; onDone: () => void }) {
  const svc = useService();
  const ts = useTranscripts();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(30);
  const [busy, setBusy] = useState<string | null>(null);
  const { data, reload } = useLoad(async () => (open ? svc.aiRoleFixes(session.id) : null), [svc, session.id, open]);
  const pick = (segmentId: string, role: 'counselor' | 'client') => {
    setBusy(segmentId);
    void ts
      .setRole(session, segmentId, role)
      .then(() => {
        reload();
        onDone();
      })
      .finally(() => setBusy(null));
  };
  return (
    <div className="todo-item">
      <strong>AI가 고친 화자 {count}줄</strong>
      <span className="small">
        "내"로 표시됐지만 상담자 말로 보이는 줄 등을 AI가 고쳤어요. 줄마다 확인하거나, 맞으면 모두 그대로 두세요.
      </span>
      <div className="row">
        <button type="button" className="primary" onClick={() => void svc.acceptAiRoleFixes(session.id).then(onDone)}>
          모두 그대로 두기
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)}>
          {open ? '줄 닫기' : '줄 보기'}
        </button>
      </div>
      {open && !data && <p className="muted small">불러오는 중…</p>}
      {open && data && (
        <ul className="role-fix-list">
          {data.slice(0, shown).map((f) => (
            <li key={f.segmentId}>
              {f.prev && (
                <p className="rf-ctx">
                  <span>{ROLE_KO[f.prev.role] ?? f.prev.role}</span> {f.prev.text}
                </p>
              )}
              <div className="rf-main">
                <span className="muted small">{mmss(f.startMs)}</span>
                <span className="status-pill">AI: {ROLE_KO[f.role] ?? f.role}</span>
                <span className="rf-text">{f.text}</span>
              </div>
              {f.next && (
                <p className="rf-ctx">
                  <span>{ROLE_KO[f.next.role] ?? f.next.role}</span> {f.next.text}
                </p>
              )}
              {f.reason && <p className="muted small rf-reason">이유: {f.reason}</p>}
              <div className="row">
                {(['counselor', 'client'] as const).map((r2) => (
                  <button
                    key={r2}
                    type="button"
                    className={f.role === r2 ? 'pill-btn on' : 'pill-btn'}
                    disabled={busy === f.segmentId}
                    onClick={() => pick(f.segmentId, r2)}
                  >
                    {ROLE_KO[r2]}
                    {f.role === r2 ? ' (그대로)' : ''}
                  </button>
                ))}
              </div>
            </li>
          ))}
          {data.length === 0 && <li className="muted small">확인할 줄이 없어요.</li>}
          {data.length > shown && (
            <li>
              <button type="button" className="link small" onClick={() => setShown((n) => n + 30)}>
                더 보기 (남은 {data.length - shown}줄)
              </button>
            </li>
          )}
        </ul>
      )}
      <Link className="small" to={`/sessions/${session.id}#transcript`}>
        전사 전체에서 보기
      </Link>
    </div>
  );
}

/** 사례개념화 개정안: 바뀌는 가설 요약, 연결 못한 의견은 여기서 바로 정리, 승인 */
function FormulationApprove({
  clientId,
  item,
  onDone,
  onDiscard,
}: {
  clientId: string;
  item: Extract<SessionTodo, { kind: 'formulation' }>;
  onDone: () => void;
  onDiscard: () => void;
}) {
  const svc = useService();
  const { data: rev, reload } = useLoad(
    async () => (await svc.listFormulationRevisions(clientId)).find((x) => x.id === item.id),
    [svc, clientId, item.id, item.opinions],
  );
  return (
    <div className="todo-item">
      <strong>사례개념화 개정안 v{item.version}</strong>
      {item.changes.length > 0 ? (
        <details>
          <summary className="small">
            바뀌는 가설 {item.changes.length}개 ({[...new Set(item.changes.map((c) => c.label))].join('·')})
          </summary>
          <ul className="small plain-list">
            {item.changes.map((c) => (
              <li key={c.label + c.text}>
                <span className="status-pill">{c.label}</span> {c.text}
              </li>
            ))}
          </ul>
        </details>
      ) : (
        <span className="small">가설 변화 표시 없음 · 내용은 자세히 보기에서</span>
      )}
      {rev && (
        <PendingOpinions
          rev={rev}
          onApply={(patch) =>
            void svc.updateFormulation(rev.id, patch).then(() => {
              reload();
              onDone();
            })
          }
        />
      )}
      <div className="row">
        <ConfirmButton
          className="primary"
          label="승인"
          confirmText={
            item.opinions
              ? `연결 못한 의견 ${item.opinions}개가 남아 있어요. 그대로 승인하면 변경 이유에 남습니다. 승인할까요?`
              : '승인하면 이 버전이 현재 사례개념화가 됩니다. 이전 버전은 그대로 보존됩니다.'
          }
          onConfirm={() => void svc.approveFormulation(item.id).then(onDone)}
        />
        <Link className="button" to={`/clients/${clientId}/formulation`}>
          자세히 보고 고치기
        </Link>
        <ConfirmButton
          className="link small"
          label="개정안 버리기"
          confirmText="이 개정안을 버릴까요? 현재(승인된) 사례개념화는 그대로이고, 버린 직후에는 되돌릴 수 있어요."
          onConfirm={onDiscard}
        />
        {(item.changes.length >= 8 || item.opinions >= 3) && (
          <Link className="button" to={`/clients/${clientId}/formulation#tidy`}>
            많으면: AI로 합쳐 정리하기
          </Link>
        )}
      </div>
    </div>
  );
}

// ---------------- 4. 검토 ----------------

/** 상담자가 판단을 확정하는 곳: 위험 → 목표 → (AI가 고친 화자) → 사례개념화 → 진단적 고려 → 치료 단계 */
function StepReview({ frame, session, onNext }: { frame: FrameProps; session: Session; onNext: () => void }) {
  const svc = useService();
  const { data: todo, reload } = useLoad(() => svc.sessionTodo(session.id), [svc, session.id, session.revision]);
  const n = todo?.length ?? 0;
  return (
    <RunFrame
      {...frame}
      title={!todo ? '불러오는 중…' : n ? `확인하고 승인할 것 ${n}개` : '확인할 것이 없어요'}
      cta={
        <>
          {n > 0 && <p className="muted small">남은 것은 나중에 해도 돼요. 진행 막대에 빠진 단계로 표시돼요.</p>}
          <button type="button" className={n ? 'run-next' : 'primary run-next'} onClick={onNext}>
            상담기록지로 →
          </button>
        </>
      }
    >
      {todo && n === 0 && <p className="done-strip">✓ 위험·목표·사례개념화·진단적 고려 모두 확인했어요.</p>}
      {todo && <ReviewList session={session} items={todo} onDone={reload} title="승인할 것" />}
      <TreatmentLine clientId={session.clientId} canAdvance />
    </RunFrame>
  );
}

// ---------------- 5. 마무리: 기록과 다음 준비 ----------------

/**
 * 승인 전: 상담기록지를 보고(칸별 고치기·Word) 승인한다.
 * 승인 후 같은 화면에서: 자동으로 만든 다음 회기 준비를 바로 확인하고, 이번 회기 대화록·축어록을 바로 만든다.
 */
function StepFinish({ frame, session, onChanged }: { frame: FrameProps; session: Session; onChanged: () => void }) {
  const svc = useService();
  const [err, setErr] = useState<string | null>(null);
  const { data, reload } = useLoad(
    async () => ({ journal: await svc.createJournalDraft(session.id), todo: await svc.sessionTodo(session.id) }),
    [svc, session.id, session.revision],
  );
  const journal = data?.journal;
  const todo = data?.todo ?? [];
  const approved = journal?.status === 'approved';
  return (
    <RunFrame
      {...frame}
      title={approved ? `${session.sessionNumber}회기 완료` : '상담기록지를 확인하고 승인해요'}
      {...(approved ? { aside: <NextPrep session={session} /> } : {})}
      cta={
        journal && !approved ? (
          <>
            <p className="muted small">
              승인하면 기록지가 잠기고 다음 회기 준비가 만들어져요. 고칠 때는 개정본으로 남아요.
            </p>
            <ConfirmButton
              className="primary run-next"
              label="상담기록지 승인"
              confirmText={
                todo.length
                  ? `검토 단계에 아직 승인하지 않은 것 ${todo.length}개가 남아 있어요. 그대로 두고 상담기록지를 승인할까요? (남은 것은 진행 막대에 빠진 단계로 표시돼요)`
                  : '승인하면 이 기록은 잠기고, 다음 회기 준비 자료가 자동으로 만들어집니다.'
              }
              onConfirm={() =>
                void svc
                  .approveJournal(journal.id)
                  .then(() => {
                    reload();
                    onChanged();
                  })
                  .catch((e: Error) => setErr(e.message))
              }
            />
          </>
        ) : undefined
      }
    >
      {approved && <p className="done-strip">✓ 상담기록지를 승인했어요. 다음 회기 준비를 만들었어요.</p>}
      {!journal && <p className="muted">상담기록지 준비 중…</p>}
      {journal && (
        <details className="card run-record" open={!approved}>
          <summary className="card-head" style={{ width: '100%' }}>
            <strong>상담기록지</strong>
            <Link to={`/sessions/${session.id}/journal`} className="small">
              {approved ? '보기 · Word로 저장' : '고치기 · Word로 저장'}
            </Link>
          </summary>
          <dl>
            {JOURNAL_FORM_FIELDS.filter((k) => journal[k]).map((k) => (
              <div key={k}>
                <dt>{JOURNAL_FIELD_LABELS[k]}</dt>
                <dd>{journal[k]}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
      {approved && <SessionDocs session={session} />}
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
    </RunFrame>
  );
}

/** 다음 회기 준비: 승인하면서 자동으로 만든 것 — 목표·확인할 질문을 바로 보고, 고칠 때만 준비 화면으로 */
function NextPrep({ session }: { session: Session }) {
  const svc = useService();
  const next = session.sessionNumber + 1;
  const { data: brief } = useLoad(
    async () => (await svc.listBriefs(session.clientId)).find((b) => b.targetSessionNumber === next),
    [svc, session.clientId, next],
  );
  return (
    <div className="card">
      <div className="card-head" style={{ width: '100%' }}>
        <strong>{next}회기 준비</strong>
        <Link to={`/clients/${session.clientId}/brief?target=${next}`} className="small">
          고치기·더 보기
        </Link>
      </div>
      {!brief && <p className="muted small">만드는 중…</p>}
      {brief && (
        <>
          <span className="muted small">이번 회기 목표</span>
          <ul className="small plain-list">
            {(brief.sessionObjectives ?? []).length ? (
              brief.sessionObjectives!.map((o) => <li key={o}>· {o}</li>)
            ) : (
              <li className="muted">아직 없음 — 고치기에서 적어 두세요</li>
            )}
          </ul>
          {(brief.assessmentQuestions ?? []).length > 0 && (
            <>
              <span className="muted small">확인할 질문</span>
              <ul className="small plain-list">
                {brief.assessmentQuestions!.slice(0, 4).map((q) => (
                  <li key={q.question}>· {q.question}</li>
                ))}
              </ul>
            </>
          )}
          {(brief.riskFollowUp ?? []).length > 0 && (
            <p className="warn small">위험 확인: {brief.riskFollowUp!.join(', ')}</p>
          )}
        </>
      )}
      <TreatmentLine clientId={session.clientId} />
    </div>
  );
}

/** 이번 회기 문서: 대화록·축어록을 추천 구간에서 바로 만들고, 사례보고서로 이어간다 */
function SessionDocs({ session }: { session: Session }) {
  const [doc, setDoc] = useState<'dialogue' | 'verbatim' | null>(null);
  return (
    <div className="card">
      <strong>이번 회기 문서</strong>
      <p className="muted small">
        추천 구간(변화가 드러난 곳)에서 골라 바로 만들어요. 만든 문서는 문서 탭에서 외부 AI로 다듬어요.
      </p>
      <div className="row">
        <button
          type="button"
          className={doc === 'dialogue' ? 'pill-btn on' : 'pill-btn'}
          onClick={() => setDoc(doc === 'dialogue' ? null : 'dialogue')}
        >
          대화록 만들기
        </button>
        <button
          type="button"
          className={doc === 'verbatim' ? 'pill-btn on' : 'pill-btn'}
          onClick={() => setDoc(doc === 'verbatim' ? null : 'verbatim')}
        >
          축어록 만들기
        </button>
        <Link className="button pill-btn" to={`/clients/${session.clientId}/documents`}>
          사례보고서·문서 →
        </Link>
      </div>
      {doc && <RangeDocMaker key={doc} session={session} kind={doc} />}
    </div>
  );
}
